/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/**
 * ------------------------------------------------------------------------------------------------------------
 * GALLERIA SULLA CARD PRODOTTO (custom Extrò)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Sulle card (collezioni, ricerca, raccomandazioni...) le foto del prodotto si scorrono muovendo il mouse da
 * sinistra a destra sull'immagine: la card è divisa in tante fasce verticali quante sono le foto. Sotto,
 * una barretta segna a che punto siamo. Su touch si usano le frecce o lo swipe orizzontale.
 *
 * Il markup arriva da snippets/product-item.liquid, lo stile da elveth.css.
 *
 * Perf: le foto oltre la prima sono stampate con `hidden` + loading="lazy", quindi il browser non le scarica
 * finché non servono davvero. Le sblocchiamo qui, al primo passaggio del mouse o al primo tocco sulla card,
 * insieme all'aggancio dei listener: finché l'utente non interagisce, questo codice non costa nulla.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-card-gallery]';
  var SWIPE_MIN_DISTANCE = 30; // px sotto i quali è un tap, non uno swipe

  function closestGallery(node) {
    return node && typeof node.closest === 'function' ? node.closest(SELECTOR) : null;
  }

  function setIndex(wrapper, index) {
    var images = wrapper.galleryImages;
    var bars = wrapper.galleryBars;
    var total = images.length + 1; // +1 = la foto principale, che è già nel markup
    var i;

    // Le frecce girano in tondo: dall'ultima si torna alla prima e viceversa
    if (index < 0) {
      index = total - 1;
    } else if (index > total - 1) {
      index = 0;
    }

    if (wrapper.galleryIndex === index) {
      return;
    }

    wrapper.galleryIndex = index;

    for (i = 0; i < images.length; i++) {
      if (i + 1 === index) {
        images[i].setAttribute('loading', 'eager');
        images[i].removeAttribute('hidden');
      }
      images[i].classList.toggle('is-active', i + 1 === index);
    }

    for (i = 0; i < bars.length; i++) {
      bars[i].classList.toggle('is-active', i === index);
    }
  }

  function onMouseMove(event, wrapper) {

    // Dopo un clic sulla freccia lo scorrimento col mouse resta fermo fino a quando non si esce dalla card,
    // altrimenti il primo movimento cancellerebbe subito la foto appena scelta
    if (wrapper.galleryLocked) {
      return;
    }

    var rect = wrapper.getBoundingClientRect();

    if (!rect.width) {
      return;
    }

    var total = wrapper.galleryImages.length + 1;
    var index = Math.floor(((event.clientX - rect.left) / rect.width) * total);

    setIndex(wrapper, Math.max(0, Math.min(total - 1, index)));
  }

  function onMouseLeave(event) {
    var wrapper = event.currentTarget;

    wrapper.galleryLocked = false;
    setIndex(wrapper, 0);
  }

  function onTouchEnd(event) {
    var wrapper = event.currentTarget;
    var touch = event.changedTouches[0];

    if (wrapper.galleryTouchX === null) {
      return;
    }

    var deltaX = touch.clientX - wrapper.galleryTouchX;
    var deltaY = touch.clientY - wrapper.galleryTouchY;

    wrapper.galleryTouchX = null;

    // Movimento troppo corto (è un tap) o prevalentemente verticale (sta scorrendo la pagina): lasciamo stare
    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    setIndex(wrapper, wrapper.galleryIndex + (deltaX < 0 ? 1 : -1));

    // Su alcuni browser dopo lo swipe parte comunque il clic sul link: lo neutralizziamo per un attimo
    wrapper.gallerySwiped = true;

    setTimeout(function () {
      wrapper.gallerySwiped = false;
    }, 400);
  }

  function onCaptureClick(event) {
    if (event.currentTarget.gallerySwiped) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function recordTouchStart(wrapper, event) {
    var touch = event.changedTouches[0];

    wrapper.galleryTouchX = touch.clientX;
    wrapper.galleryTouchY = touch.clientY;
  }

  function init(wrapper) {
    if (wrapper.galleryReady) {
      return;
    }

    wrapper.galleryReady = true;
    wrapper.galleryImages = Array.prototype.slice.call(wrapper.querySelectorAll('.product-item__gallery-image'));
    wrapper.galleryBars = Array.prototype.slice.call(wrapper.querySelectorAll('.product-item__gallery-bar-item'));
    wrapper.galleryIndex = 0;
    wrapper.galleryLocked = false;
    wrapper.gallerySwiped = false;
    wrapper.galleryTouchX = null;

    wrapper.addEventListener('mouseleave', onMouseLeave);

    wrapper.addEventListener('touchend', onTouchEnd, { passive: true });
    wrapper.addEventListener('click', onCaptureClick, true);
  }

  // Delegazione: funziona anche su card aggiunte dall'editor o caricate dopo il primo hover.
  // Conta il puntatore effettivo, anche sui portatili con touchscreen.
  document.addEventListener(window.PointerEvent ? 'pointermove' : 'mousemove', function (event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    var wrapper = closestGallery(event.target);
    if (!wrapper) return;
    init(wrapper);
    onMouseMove(event, wrapper);
  }, { passive: true });

  document.addEventListener('touchstart', function (event) {
    var wrapper = closestGallery(event.target);

    if (!wrapper) {
      return;
    }

    init(wrapper);
    recordTouchStart(wrapper, event);
  }, { passive: true });

  document.addEventListener('click', function (event) {
    var arrow = typeof event.target.closest === 'function' ? event.target.closest('.product-item__gallery-arrow') : null;

    if (!arrow) {
      return;
    }

    event.preventDefault();

    var wrapper = closestGallery(arrow);

    if (!wrapper) {
      return;
    }

    init(wrapper);

    wrapper.galleryLocked = true;
    setIndex(wrapper, wrapper.galleryIndex + Number(arrow.getAttribute('data-gallery-step')));
  });
})();
